(function () {
  class EbCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.eb-cart-drawer__panel');
      this.toast = this.querySelector('[data-eb-cart-toast]');
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.lastPageScrollY = window.scrollY;
      this.boundOpen = (event) => this.openFromEvent(event);
      this.boundAdd = (event) => this.refreshFromAddEvent(event);
      this.openSequence = 0;
      this.boundRememberScroll = () => {
        if (this.ignoreScrollMemory) return;
        const scrollY = window.scrollY;
        window.clearTimeout(this.scrollMemoryTimer);
        this.scrollMemoryTimer = window.setTimeout(() => {
          if (!this.ignoreScrollMemory) this.lastPageScrollY = scrollY;
        }, 140);
      };
      this.boundSectionSelect = (event) => {
        if (!this.isOwnSectionEvent(event)) return;
        this.restorePageScroll(this.lastPageScrollY);
      };
      document.addEventListener('eb:cart:open', this.boundOpen);
      document.addEventListener('eb:cart:add', this.boundAdd);
      if (this.isDesignMode) {
        window.addEventListener('scroll', this.boundRememberScroll, { passive: true });
        document.addEventListener('shopify:section:select', this.boundSectionSelect);
      }
      this.bind();
      this.loadRecommendations();
      this.updateEtaFromAnnouncement();
    }

    disconnectedCallback() {
      document.removeEventListener('eb:cart:open', this.boundOpen);
      document.removeEventListener('eb:cart:add', this.boundAdd);
      window.removeEventListener('scroll', this.boundRememberScroll);
      document.removeEventListener('shopify:section:select', this.boundSectionSelect);
    }

    bind() {
      if (!this.boundInternalClick) {
        this.boundInternalClick = (event) => {
          const close = event.target.closest('[data-eb-cart-close]');
          if (close) {
            event.preventDefault();
            this.close();
            return;
          }

          const change = event.target.closest('[data-eb-cart-change]');
          if (change) {
            event.preventDefault();
            this.changeLine(change.dataset.key, Number(change.dataset.ebCartChange), Number(change.dataset.line || 0));
            return;
          }

          const remove = event.target.closest('[data-eb-cart-remove]');
          if (remove) {
            event.preventDefault();
            this.removeLine(remove.dataset.key, Number(remove.dataset.line || 0));
            return;
          }

          const rec = event.target.closest('[data-eb-rec-add]');
          if (rec) {
            event.preventDefault();
            this.addRecommendation(Number(rec.dataset.ebRecAdd));
          }
        };
      }

      if (!this.boundInternalKeydown) {
        this.boundInternalKeydown = (event) => {
          if (event.key === 'Escape') this.close();
          if (event.key === 'Tab') this.trapFocus(event);
        };
      }

      if (!this.boundInternalSubmit) {
        this.boundInternalSubmit = (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) return;
          this.prepareCheckoutForm(form);
        };
      }

      this.removeEventListener('click', this.boundInternalClick);
      this.addEventListener('click', this.boundInternalClick);
      this.removeEventListener('keydown', this.boundInternalKeydown);
      this.addEventListener('keydown', this.boundInternalKeydown);
      this.removeEventListener('submit', this.boundInternalSubmit);
      this.addEventListener('submit', this.boundInternalSubmit);

      this.querySelector('[data-eb-cart-note]')?.addEventListener('input', this.debounce((event) => {
        fetch(this.cartUrl('update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: event.target.value }),
        }).catch(() => {});
      }, 400));
    }

    async openFromEvent(event) {
      if (this.isOpening) return;
      this.isOpening = true;
      const sequence = this.openSequence + 1;
      this.openSequence = sequence;
      this.lastTrigger = event.target instanceof HTMLElement ? event.target : document.activeElement;
      const restoreScrollY = this.isDesignMode ? window.scrollY : null;
      const variantIds = (event.detail?.lines || []).map((line) => String(line.id));
      this.open({ restoreScrollY, focus: false });
      try {
        await this.refresh(variantIds);
      } catch (error) {
        // Keep the current drawer markup usable if the section refresh is blocked in the editor.
      }
      if (this.openSequence === sequence && this.classList.contains('is-open')) this.open({ restoreScrollY });
      window.setTimeout(() => {
        if (this.openSequence === sequence) this.isOpening = false;
      }, 120);
    }

    async refreshFromAddEvent(event) {
      if (!this.classList.contains('is-open')) return;
      const variantIds = (event.detail?.lines || []).map((line) => String(line.id));
      try {
        await this.refresh(variantIds);
      } catch (error) {
        // Keep the existing drawer state if the background refresh is blocked in the editor.
      }
    }

    open(options = {}) {
      const { restoreScrollY = null, focus = true } = options;
      const wasOpen = this.classList.contains('is-open');
      if (this.isDesignMode && !wasOpen) {
        this.openScrollY = restoreScrollY ?? window.scrollY;
      }
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (!this.isDesignMode && !wasOpen) {
        window.ebScrollLock?.lock();
        this.setPageInert(true);
      }
      if (restoreScrollY !== null) this.restorePageScroll(restoreScrollY);
      if (focus) {
        window.setTimeout(() => {
          this.panel?.focus?.({ preventScroll: true });
          if (restoreScrollY !== null) this.restorePageScroll(restoreScrollY);
        }, 30);
      }
    }

    close() {
      this.openSequence += 1;
      this.isOpening = false;
      const wasOpen = this.classList.contains('is-open');
      const restoreScrollY = this.isDesignMode ? (this.openScrollY ?? this.lastPageScrollY ?? window.scrollY) : null;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (wasOpen && !this.isDesignMode) window.ebScrollLock?.unlock();
      if (!this.isDesignMode) this.setPageInert(false);
      if (wasOpen) this.dispatchEvent(new CustomEvent('eb:cart:close', { bubbles: true }));
      this.lastTrigger?.focus?.({ preventScroll: true });
      if (restoreScrollY !== null) this.restorePageScroll(restoreScrollY);
    }

    prepareCheckoutForm(form) {
      this.syncHiddenField(form, 'note', this.querySelector('[data-eb-cart-note]')?.value || '');
      this.syncHiddenField(form, 'discount', this.querySelector('[data-eb-discount-code]')?.value || '');
    }

    syncHiddenField(form, name, value) {
      let input = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"]`);
      const normalized = String(value || '').trim();
      if (!normalized) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = normalized;
    }

    isOwnSectionEvent(event) {
      const eventSectionId = event.detail?.sectionId;
      if (eventSectionId && eventSectionId === this.dataset.sectionId) return true;
      const sectionRoot = this.closest('[id^="shopify-section-"]');
      return Boolean(sectionRoot && event.target === sectionRoot);
    }

    restorePageScroll(scrollY) {
      if (!this.isDesignMode || typeof scrollY !== 'number') return;
      this.ignoreScrollMemory = true;
      const restore = () => window.scrollTo({ left: window.scrollX, top: scrollY, behavior: 'auto' });
      restore();
      window.requestAnimationFrame(restore);
      window.setTimeout(restore, 80);
      window.setTimeout(() => {
        this.ignoreScrollMemory = false;
        this.lastPageScrollY = window.scrollY;
      }, 180);
    }

    setPageInert(state) {
      [...document.body.children].forEach((child) => {
        if (child === this || child.contains(this) || child.tagName === 'SCRIPT') return;
        if (state) child.setAttribute('inert', '');
        else child.removeAttribute('inert');
      });
    }

    trapFocus(event) {
      const focusable = [...this.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async refresh(highlightVariantIds = []) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((response) => response.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('eb-cart-drawer');
      if (!fresh) return;
      this.innerHTML = fresh.innerHTML;
      this.panel = this.querySelector('.eb-cart-drawer__panel');
      this.toast = this.querySelector('[data-eb-cart-toast]');
      this.bind();
      highlightVariantIds.forEach((id) => {
        this.querySelector(`[data-variant-id="${CSS.escape(id)}"]`)?.classList.add('is-new');
      });
      this.loadRecommendations();
      this.updateEtaFromAnnouncement();
      document.dispatchEvent(new CustomEvent('eb:cart:updated'));
    }

    async changeLine(key, quantity, line = 0) {
      const row = this.findRow(key, line);
      if (row?.classList.contains('is-updating')) return;
      const oldQuantity = Number(row?.querySelector('[data-eb-line-quantity]')?.textContent || 0);
      if (quantity <= 0) {
        this.removeLine(key, line);
        return;
      }
      row?.querySelector('[data-eb-line-quantity]')?.replaceChildren(document.createTextNode(String(quantity)));
      this.setLineBusy(row, true);
      try {
        await this.postCartChange(key, quantity, line);
        await this.refresh();
        this.open();
      } catch (error) {
        row?.querySelector('[data-eb-line-quantity]')?.replaceChildren(document.createTextNode(String(oldQuantity)));
        this.setLineStatus(row, this.dataset.errorLabel);
      } finally {
        this.setLineBusy(row, false);
      }
    }

    async removeLine(key, line = 0) {
      const cart = await fetch(this.cartUrl('cart.js')).then((response) => response.json()).catch(() => null);
      const removed = cart?.items?.filter((item) => item.key === key) || [];
      const row = this.findRow(key, line);
      if (row?.classList.contains('is-updating')) return;
      const variantId = row?.dataset.variantId;
      const childRows = [...this.querySelectorAll(`[data-bundle-parent="${CSS.escape(String(variantId))}"]`)];
      if (childRows.length && !window.confirm(this.dataset.removeBundleLabel)) return;
      childRows.forEach((child) => {
        const childItem = cart?.items?.find((item) => item.key === child.dataset.lineKey);
        if (childItem) removed.push(childItem);
      });
      this.setLineBusy(row, true);
      row?.classList.add('is-removing');
      childRows.forEach((child) => child.classList.add('is-removing'));
      try {
        await this.removeKeys([
          { key, line },
          ...childRows.map((child) => ({ key: child.dataset.lineKey, line: Number(child.dataset.lineIndex || 0) })),
        ]);
        await this.refresh();
        this.open();
        this.showUndo(removed);
      } catch (error) {
        row?.classList.remove('is-removing');
        this.setLineStatus(row, this.dataset.errorLabel);
      } finally {
        this.setLineBusy(row, false);
      }
    }

    async removeKeys(items) {
      for (const item of items) {
        await this.postCartChange(item.key, 0, item.line);
      }
    }

    showUndo(items) {
      if (!this.toast || !items.length) return;
      this.toast.innerHTML = `${this.escape(this.dataset.removedLabel)} <button type="button">${this.escape(this.dataset.undoLabel)}</button>`;
      this.toast.classList.add('is-visible');
      const timer = window.setTimeout(() => this.toast?.classList.remove('is-visible'), 5000);
      this.toast.querySelector('button')?.addEventListener('click', async () => {
        window.clearTimeout(timer);
        await fetch(this.cartUrl('add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items.map((item) => ({
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties,
            })),
          }),
        });
        await this.refresh(items.map((item) => item.variant_id));
        this.open();
      }, { once: true });
    }

    setLineStatus(row, message) {
      const status = row?.querySelector('[data-eb-line-status]');
      if (status) status.textContent = message || '';
    }

    setLineBusy(row, state) {
      if (!row) return;
      row.classList.toggle('is-updating', state);
      row.toggleAttribute('aria-busy', state);
      row.querySelectorAll('button').forEach((button) => {
        button.disabled = state;
      });
    }

    findRow(key, line = 0) {
      if (key) {
        const byKey = this.querySelector(`[data-line-key="${CSS.escape(key)}"]`);
        if (byKey) return byKey;
      }
      if (line > 0) return this.querySelector(`[data-line-index="${CSS.escape(String(line))}"]`);
      return null;
    }

    async postCartChange(key, quantity, line = 0) {
      const payloads = [];
      if (key) payloads.push({ id: key, quantity });
      if (line > 0) payloads.push({ line, quantity });

      let lastError;
      for (const payload of payloads) {
        try {
          return await fetch(this.cartUrl('change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          }).then((response) => {
            if (!response.ok) throw response;
            return response.json();
          });
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError || new Error('Unable to update cart');
    }

    cartUrl(endpoint) {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return endpoint === 'cart.js' ? `${normalizedRoot}cart.js` : `${normalizedRoot}cart/${endpoint}`;
    }

    async addRecommendation(variantId) {
      await fetch(this.cartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const scrollTop = this.querySelector('.eb-cart-drawer__content')?.scrollTop || 0;
      await this.refresh([String(variantId)]);
      this.querySelector('.eb-cart-drawer__content')?.scrollTo({ top: scrollTop });
      this.open();
    }

    async loadRecommendations() {
      const target = this.querySelector('[data-eb-recommendations][data-product-id]');
      if (!target || target.dataset.loaded) return;
      target.dataset.loaded = 'true';
      const url = `/recommendations/products?section_id=${encodeURIComponent(this.dataset.sectionId)}&product_id=${encodeURIComponent(target.dataset.productId)}&intent=complementary`;
      const html = await fetch(url).then((response) => response.text()).catch(() => '');
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-eb-recommendation-list]');
      const list = target.querySelector('[data-eb-recommendation-list]');
      if (fresh && list) list.innerHTML = fresh.innerHTML;
    }

    updateEtaFromAnnouncement() {
      const output = this.querySelector('[data-eb-cart-eta]');
      const source = document.querySelector('eb-delivery-eta');
      const api = window.EbAnnouncementTime;
      if (!output || !source || !api) return;
      const range = api.computeEtaRange({
        nowMs: Date.now(),
        countryCode: source.dataset.countryCode,
        countryName: source.dataset.countryName,
        leadTimes: api.parseLeadTimes(source.dataset.leadTimes || ''),
        cutoffHour: Number(source.dataset.cutoffHour || 0),
        skipWeekends: source.dataset.skipWeekends === 'true',
        storeTimeZone: source.dataset.timezone || 'UTC',
        locale: source.dataset.locale || undefined,
      });
      if (range) output.innerHTML = api.formatEtaText(source.dataset.template, range);
    }

    debounce(callback, wait) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(...args), wait);
      };
    }

    escape(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char]);
    }
  }

  if (!customElements.get('eb-cart-drawer')) customElements.define('eb-cart-drawer', EbCartDrawer);
})();

